/*
 * Copyright (C) GMS Inc - All Rights Reserved
 * This file is part of GMSCloud. - Proprietary and confidential
 * Unauthorized copying of this file, via any medium is strictly prohibited
 *
 * Created on   : 17-Jul-2026
 * @author      : Vincy R
 * Requirement# : 22300
 * Purpose      : Trade Templates orchestrator — Library / Builder / Versions with provider/global/tenant
 *                scoping, per-user draft auto-recovery, and API-backed save/draft/promote/makeCurrent.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SplitPane, { Pane } from 'split-pane-react-fixed';
import 'split-pane-react-fixed/esm/themes/default.css';
import { NT, NI } from '../NaomiTheme';

import { Trade, Tenant, GlobalDefaultTemplate, Scope, Task, PhaseId, TemplateVersion, ForkPointer, AseVersion } from './types';
import {
  activeVersion, scopeVersionsFor, scopeLabel, appendVersion, deriveParentUid,
  nextTopLevelUid, nextChildUid, nextOrderFor, phaseRootUid, forkedFromLabel, isMilestoneUid, taskDepth,
  removeTaskAndDescendants, hasAnyDependency, cascadeRename, insertSiblingAfter,
} from './model';

import { relativeWhen } from './library/libraryModel';
import TaskOutlineTree from './builder/TaskOutlineTree';
import TaskCanvas from './builder/TaskCanvas';
import TaskInspector from './builder/TaskInspector';
import TradeTemplateLibrary from './TradeTemplateLibrary';
import TradeVersionHistory from './TradeVersionHistory';
import SaveTargetModal, { SaveTargets } from './modal/SaveTargetModal';
import PromoteModal from './modal/PromoteModal';

export interface BuilderCtx { tradeId: string | null; scope: Scope; tenantId: string | null; version: string; aseVersion?: AseVersion; }

/** API seam — the page wires these to TradeTemplateVersionService and reloads on success. */
export interface TradeTemplatePersist {
  saveNewVersion: (ctx: BuilderCtx, tasks: Task[], targets: SaveTargets) => Promise<void>;
  saveDraft: (ctx: BuilderCtx, tasks: Task[]) => Promise<void>;
  makeCurrent: (ctx: BuilderCtx, version: string, versionGroupUid: string | null | undefined) => Promise<void>;
  promote: (trade: Trade, tenantId: string, sourceVersion: string) => Promise<void>;
  listVersions: (scope: Scope, tradeId: string | null, tenantId: string | null) => Promise<TemplateVersion[]>;
  /** Resolve the current template for a trade with two-tier fallback (provider default, else global). */
  resolveCurrent: (tradeId: string) => Promise<{ source: 'provider' | 'global' | 'none'; tasks: Task[] }>;
  /** Read the scope's saved (status='draft') work-in-progress so the Builder can offer to resume it. */
  loadDraft: (scope: Scope, tradeId: string | null, tenantId: string | null) => Promise<{ hasDraft: boolean; tasks: Task[]; when: string | null; forkedFrom: ForkPointer | null }>;
}

export interface TradeTemplateSystemProps {
  trades: Trade[];
  tenants: Tenant[];
  globalDefault: GlobalDefaultTemplate;
  oid: string;                    // current user's oid — namespaces the per-user draft autosave
  persist: TradeTemplatePersist;
  onReload?: () => void;          // called after a successful server mutation to refetch
  onSelectTenant?: (tenantId: string) => void; // lazy-load a tenant's versions (By-tenant library view)
}

const AUTOSAVE_PREFIX = 'trade_template_autosave_';

type TabId = 'library' | 'builder' | 'versions';
/** Tab set for the segmented control — mirrors NAOMI Studio's tab bar (NI icon + label). */
const TT_TABS: { id: TabId; label: string; icon: React.ReactNode }[] = [
  { id: 'library', label: 'Library', icon: <NI.Book /> },
  { id: 'builder', label: 'Builder', icon: <NI.Edit /> },
  { id: 'versions', label: 'Versions', icon: <NI.History /> },
];

/** Top-level orchestrator: Library ⇄ Builder ⇄ Versions, provider/global/tenant scoping. */
export default function TradeTemplateSystem({ trades, tenants, globalDefault, oid, persist, onReload, onSelectTenant }: TradeTemplateSystemProps) {
  const [view, setView] = useState<'library' | 'builder' | 'versions'>('library');
  const [ctx, setCtx] = useState<BuilderCtx | null>(null);
  const [overrides, setOverrides] = useState<Record<string, ReturnType<typeof appendVersion>['all']>>({});
  const [draftTasks, setDraftTasks] = useState<Task[] | null>(null);
  const [selectedUid, setSelectedUid] = useState<string | null>(null);
  const [activePhase, setActivePhase] = useState<PhaseId>('sales');
  // Target ASE engine for the template being built. '2.0' = full 21-field contract; '1.0' = legacy
  // 14-field shape (hides the seven 2.0-only editors and omits those fields on save).
  const [aseVersion, setAseVersion] = useState<AseVersion>('2.0');
  // Builder's 3-way split (outline tree | canvas | inspector) — user-draggable via SplitPane; sizes
  // are session-only (not persisted), matching the panel's prior fixed defaults (280 / auto / 340).
  const [paneSizes, setPaneSizes] = useState<(number | string)[]>([280, 'auto', 340]);

  const [saveOpen, setSaveOpen] = useState(false);
  // Gates Save Draft / Publish… when NOT EVEN ONE task in the template has a dependency set (see
  // hasAnyDependency) — shown BEFORE the save proceeds (not as an after-the-fact toast, which was
  // too easy to miss), so the user can go back and add one instead of only finding out afterward.
  const [depWarnFor, setDepWarnFor] = useState<'publish' | 'draft' | null>(null);
  const [promote, setPromote] = useState<{ trade: Trade; tenantId: string; sourceVersion: string } | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [recovery, setRecovery] = useState<{ key: string; tasks: Task[] } | null>(null);
  const [busy, setBusy] = useState(false);
  // True while a freshly-opened scope resolves its versions / seed template. Shows a spinner in the
  // builder body instead of the empty "No tasks" state during that async window.
  const [scopeLoading, setScopeLoading] = useState(false);
  // When a tenant scope has no version of its own, the builder is seeded from the provider default
  // ('provider') or the Global Default ('global') as an unsaved starting point; null = its own version.
  const [seededFrom, setSeededFrom] = useState<'provider' | 'global' | null>(null);
  // A saved (status='draft') DB draft for the open scope, offered via a Resume banner. null = none/dismissed.
  const [dbDraft, setDbDraft] = useState<{ tasks: Task[]; when: string | null; forkedFrom: ForkPointer | null } | null>(null);
  // True when the builder is editing the scope's DRAFT (opened via the Draft chip / resumed) rather than
  // the published version — drives the "Update draft" vs "Save draft" label. Set synchronously from the
  // open intent so it never flips mid-load or shows "Update draft" while editing the published template.
  const [editingDraft, setEditingDraft] = useState(false);
  // Fork lineage of the draft currently loaded in the builder (null unless editing a forked draft).
  const [draftLineage, setDraftLineage] = useState<ForkPointer | null>(null);
  // Monotonic id of the latest openScope call — guards its async resolution against a newer open
  // (rapidly switching scopes must not let a late response bleed onto the now-current view).
  const openReqRef = useRef(0);

  const trade = ctx?.tradeId ? trades.find(t => t.id === ctx.tradeId) ?? null : null;
  const scopeKey = ctx ? `${ctx.tradeId ?? 'global'}:${ctx.scope}:${ctx.tenantId ?? ''}` : '';
  const autosaveKey = ctx ? `${AUTOSAVE_PREFIX}${oid || 'unknown'}:${scopeKey}` : '';

  function versionsFor(tradeId: string | null, scope: Scope, tenantId: string | null) {
    const key = `${tradeId ?? 'global'}:${scope}:${tenantId ?? ''}`;
    return overrides[key] ?? scopeVersionsFor(tradeId ? trades.find(t => t.id === tradeId) ?? null : null, globalDefault, scope, tenantId);
  }
  const versions = ctx ? versionsFor(ctx.tradeId, ctx.scope, ctx.tenantId) : [];
  const versionData = versions.find(v => v.version === ctx?.version);

  function applyScopeTasks(scope: Scope, vs: ReturnType<typeof appendVersion>['all']) {
    const cur = vs.find(v => v.invalid === false) ?? vs[vs.length - 1];
    const tasks = cur?.tasks ?? [];
    setSelectedUid(tasks[0]?.task_uid ?? null);
    setActivePhase(tasks[0]?.phase ?? 'sales');
    return cur;
  }

  function openScope(scope: Scope, tenantId: string | null, tradeId: string | null, openDraft = false) {
    const key = `${tradeId ?? 'global'}:${scope}:${tenantId ?? ''}`;
    const reqId = ++openReqRef.current; // this open supersedes any in-flight one
    setDraftTasks(null);
    setSeededFrom(null);
    setDbDraft(null);
    setEditingDraft(openDraft); // editing the draft iff opened via the Draft chip — set now, no async flip
    setDraftLineage(null);
    setScopeLoading(true);
    // Optimistic: show whatever we already have while the full history loads.
    const seed = versionsFor(tradeId, scope, tenantId);
    setCtx({ tradeId, scope, tenantId, version: activeVersion(seed)?.version ?? '' });
    applyScopeTasks(scope, seed);
    setView('builder');

    // Offer to resume a per-user local draft for this exact scope, if one exists.
    const autoKey = `${AUTOSAVE_PREFIX}${oid || 'unknown'}:${key}`;
    try {
      const raw = window.localStorage.getItem(autoKey);
      if (raw) {
        const parsed = JSON.parse(raw) as Task[];
        if (Array.isArray(parsed) && parsed.length) setRecovery({ key: autoKey, tasks: parsed });
      }
    } catch {
      // ignore malformed autosave
    }

    // Lazy-load the per-scope version history AND the saved (status='draft') draft in parallel.
    // Drafts exist only for provider/tenant scopes (never the Global Default).
    const noDraft = { hasDraft: false, tasks: [] as Task[], when: null, forkedFrom: null as ForkPointer | null };
    const draftP = (scope !== 'global' && tradeId)
      ? persist.loadDraft(scope, tradeId, tenantId).catch(() => noDraft)
      : Promise.resolve(noDraft);

    void Promise.all([
      persist.listVersions(scope, tradeId, tenantId).catch(() => [] as TemplateVersion[]),
      draftP,
    ]).then(async ([vs, draft]) => {
      if (openReqRef.current !== reqId) return; // a newer openScope has taken over — drop this result
      if (vs.length) {
        setOverrides(o => ({ ...o, [key]: vs }));
        const cur = applyScopeTasks(scope, vs);
        setCtx(c => (c && c.tradeId === tradeId && c.scope === scope && c.tenantId === tenantId
          ? { ...c, version: cur?.version ?? c.version } : c));
      } else if ((scope === 'tenant' || scope === 'provider') && tradeId && !draft.hasDraft) {
        // No published version AND no saved draft — seed the builder from the trade's provider default,
        // else the Global Default (two-tier resolve), as an unsaved starting point. For scope='provider'
        // itself (the "+ Add a provider default" flow) there's by definition no provider version yet, so
        // resolveCurrent naturally falls through to the Global Default.
        try {
          const { source, tasks } = await persist.resolveCurrent(tradeId);
          if (openReqRef.current !== reqId) return; // superseded during the resolve
          if (source !== 'none' && tasks.length) {
            setDraftTasks(tasks);
            setSeededFrom(source);
            setSelectedUid(tasks[0]?.task_uid ?? null);
            setActivePhase(tasks[0]?.phase ?? 'sales');
          }
        } catch { /* leave an empty builder to start from scratch */ }
      }
      // Draft handling. If opened via the "Draft" chip, load the draft straight into the builder;
      // otherwise (opened as the published template) just offer to resume it via a banner.
      if (draft.hasDraft) {
        if (openDraft && draft.tasks.length) {
          setDraftTasks(draft.tasks);
          setSelectedUid(draft.tasks[0]?.task_uid ?? null);
          setActivePhase(draft.tasks[0]?.phase ?? 'sales');
          setSeededFrom(null);
          setDbDraft(null);
          setDraftLineage(draft.forkedFrom);
          setCtx(c => (c && c.tradeId === tradeId && c.scope === scope && c.tenantId === tenantId ? { ...c, version: 'draft' } : c));
        } else if (draft.tasks.length) {
          setDbDraft({ tasks: draft.tasks, when: draft.when, forkedFrom: draft.forkedFrom });
        }
      }
    }).catch(() => { /* keep optimistic view */ })
      .finally(() => { if (openReqRef.current === reqId) setScopeLoading(false); });
  }

  function clearAutosave() {
    if (!autosaveKey) return;
    try { window.localStorage.removeItem(autosaveKey); } catch { /* noop */ }
  }

  const currentTasks = draftTasks ?? versionData?.tasks ?? [];

  // Per-user, per-scope debounced auto-recovery buffer (mirrors NAOMI Studio's localStorage autosave).
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!autosaveKey || draftTasks === null) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      try { window.localStorage.setItem(autosaveKey, JSON.stringify(draftTasks)); } catch { /* quota */ }
    }, 600);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [draftTasks, autosaveKey]);

  async function run(label: string, fn: () => Promise<void>) {
    setBusy(true);
    try {
      await fn();
      setToast(label);
      onReload?.();
    } catch (e) {
      setToast(`Failed: ${(e as Error)?.message ?? 'unknown error'}`);
    } finally {
      setBusy(false);
    }
  }

  function saveOptimistic(tradeId: string | null, scope: Scope, tenantId: string | null, tasks: Task[], note: string, forkedFrom: Parameters<typeof appendVersion>[3] = null) {
    const key = `${tradeId ?? 'global'}:${scope}:${tenantId ?? ''}`;
    const { all } = appendVersion(versionsFor(tradeId, scope, tenantId), tasks, note, forkedFrom);
    setOverrides(o => ({ ...o, [key]: all }));
  }

  function handleSave(targets: SaveTargets) {
    if (!ctx) return;
    const tasks = draftTasks ?? versionData?.tasks ?? [];
    setSaveOpen(false);
    void run('Saved new version(s)', async () => {
      await persist.saveNewVersion({ ...ctx, aseVersion }, tasks, targets);
      if (targets.toProvider) saveOptimistic(ctx.tradeId, 'provider', null, tasks, targets.note);
      if (targets.toCurrentTenant && ctx.tenantId) saveOptimistic(ctx.tradeId, 'tenant', ctx.tenantId, tasks, targets.note);
      // targets.tenantIds only appears while editing a trade's Provider default (ctx.tenantId is null
      // there) — mirror the push into each chosen tenant (all or a specific subset) for immediate UI feedback.
      targets.tenantIds.forEach(tid => saveOptimistic(ctx.tradeId, 'tenant', tid, tasks, targets.note));
      clearAutosave();
      setSeededFrom(null);
      setDbDraft(null);
      setEditingDraft(false); // publishing consumes the scope's draft (backend deleteDraftEntry)
      // Offer to promote only when they saved to the tenant WITHOUT also seeding the provider default
      // (a global-seed save that already ticks Provider needs no follow-up prompt).
      if (!targets.toProvider && targets.toCurrentTenant && ctx.tenantId && ctx.scope === 'tenant' && trade && !trade.provider) {
        setPromote({ trade, tenantId: ctx.tenantId, sourceVersion: ctx.version });
      }
    });
  }

  function handleSaveDraft() {
    if (!ctx) return;
    const tasks = draftTasks ?? versionData?.tasks ?? [];
    void run(editingDraft ? 'Draft updated' : 'Draft saved', async () => {
      await persist.saveDraft({ ...ctx, aseVersion }, tasks);
      setEditingDraft(true); // after the first save we're now editing an existing draft
    });
  }

  function makeActive(version: string) {
    if (!ctx) return;
    const vg = versionsFor(ctx.tradeId, ctx.scope, ctx.tenantId).find(v => v.version === version)?.versionGroupUid;
    void run(`${version} is now the current version`, async () => {
      await persist.makeCurrent(ctx, version, vg);
      const list = versionsFor(ctx.tradeId, ctx.scope, ctx.tenantId).map(v => ({ ...v, invalid: v.version !== version }));
      setOverrides(o => ({ ...o, [scopeKey]: list }));
      setCtx(c => c && { ...c, version });
    });
  }


  // ── Task CRUD wired to WBS auto-numbering + milestone auto-derivation (see model.ts) ──
  function updateTask(uid: string, patch: Partial<Task>, renameFrom?: string) {
    const base = draftTasks ?? versionData?.tasks ?? [];
    let tasks = base.map(t => (t.task_uid === uid ? { ...t, ...patch } : t));
    if (renameFrom && patch.task_uid) {
      // cascadeRename rewrites every descendant's task_uid prefix (not just parent_task_uid) and
      // re-slots the moved subtree's task_order — see model.ts for why the uid rewrite matters.
      tasks = cascadeRename(tasks, renameFrom, patch.task_uid);
    }
    setDraftTasks(tasks);
    if (renameFrom && patch.task_uid) setSelectedUid(patch.task_uid);
  }
  function addTask(phase: PhaseId) {
    const tasks = draftTasks ?? versionData?.tasks ?? [];
    const root = phaseRootUid(tasks, phase);
    if (root) { addChild(root); return; }
    const uid = nextTopLevelUid(tasks);
    const t = blankTask(uid, 'New task', phase, nextOrderFor(tasks, ''));
    setDraftTasks([...tasks, t]); setSelectedUid(uid); setActivePhase(phase);
  }
  function addChild(parentUid: string) {
    const tasks = draftTasks ?? versionData?.tasks ?? [];
    const parent = tasks.find(t => t.task_uid === parentUid);
    if (!parent) return;
    const uid = nextChildUid(tasks, parentUid);
    // The parent's own Est. days/hours become a DERIVED sum of its children the moment it gets its
    // first one (see computeEstimate) — never directly editable again. Carry whatever timing it had
    // as a leaf onto this first child instead of discarding it, so the total doesn't silently drop.
    const wasLeaf = !isMilestoneUid(tasks, parentUid);
    const t = blankTask(uid, 'New sub-task', parent.phase, nextOrderFor(tasks, parentUid),
      wasLeaf ? parent.estimatedDays : undefined, wasLeaf ? parent.estimatedHours : undefined);
    const next = wasLeaf
      ? tasks.map(x => (x.task_uid === parentUid ? { ...x, estimatedDays: 0, estimatedHours: 0 } : x))
      : tasks;
    setDraftTasks([...next, t]); setSelectedUid(uid); setActivePhase(parent.phase);
  }
  // Adds a new "main task" as a sibling slotted right after afterUid — NOT nested under it (addChild
  // does that) — for when the user wants to insert a task in the middle of an existing sequence
  // instead of only being able to append at the end or nest one level deeper.
  function insertTaskAfter(afterUid: string) {
    // A phase root is never a valid insertion anchor — it would mint a disconnected 5th phase entry.
    if (taskDepth(afterUid) === 0) return;
    const tasks = draftTasks ?? versionData?.tasks ?? [];
    const after = tasks.find(t => t.task_uid === afterUid);
    if (!after) return;
    const { tasks: shifted, newUid, newOrder } = insertSiblingAfter(tasks, afterUid);
    const t = blankTask(newUid, 'New task', after.phase, newOrder);
    setDraftTasks([...shifted, t]); setSelectedUid(newUid); setActivePhase(after.phase);
  }
  function deleteTask(uid: string) {
    // A phase root (depth 0, e.g. "1") is never deletable — the 4 phases must always exist, and
    // deleting it would cascade-delete the entire phase below (see removeTaskAndDescendants).
    if (taskDepth(uid) === 0) return;
    // Cascade: remove the task, all descendants (WBS supports arbitrary depth, e.g. 1.2 → 1.2.1 →
    // 1.2.1.1), AND any dangling dependency reference left pointing at one of them.
    const prefix = uid + '.';
    const tasks = removeTaskAndDescendants(draftTasks ?? versionData?.tasks ?? [], uid);
    setDraftTasks(tasks);
    if (selectedUid === uid || selectedUid?.startsWith(prefix)) setSelectedUid(null);
  }
  function duplicateTask(uid: string) {
    // A phase root is never duplicable — it would mint a second, disconnected top-level "phase" entry.
    if (taskDepth(uid) === 0) return;
    const tasks = draftTasks ?? versionData?.tasks ?? [];
    const t = tasks.find(x => x.task_uid === uid);
    if (!t) return;
    // Give the copy a valid next WBS under the same parent (never "<uid>-copy", which breaks the
    // numeric WBS contract ASE expects). Children are not deep-copied — the copy starts as a leaf.
    const parent = deriveParentUid(t.task_uid);
    const newUid = parent ? nextChildUid(tasks, parent) : nextTopLevelUid(tasks);
    setDraftTasks([...tasks, { ...t, task_uid: newUid, parent_task_uid: parent, task_order: nextOrderFor(tasks, parent), taskName: t.taskName + ' (copy)' }]);
    setSelectedUid(newUid);
  }

  const visibleTasks = useMemo(() => currentTasks.filter(t => t.valid !== false && t.status !== 'inactive'), [currentTasks]);
  const selectedTask = visibleTasks.find(t => t.task_uid === selectedUid) ?? null;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <AppBar position="static" color="inherit" elevation={0} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Toolbar variant="dense" sx={{ gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 26, height: 26, borderRadius: '7px', bgcolor: NT.primary, color: '#fff', display: 'inline-flex',
              alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 14, flexShrink: 0 }}>N</Box>
            <Typography variant="subtitle1" fontWeight={700}>Trade Templates</Typography>
          </Box>
          {/* Segmented pill tab control — matches NAOMI Studio's tab design (NT tokens). */}
          <Box sx={{ display: 'flex', bgcolor: NT.surfaceAlt, borderRadius: '8px', p: '2px' }}>
            {TT_TABS.map(t => {
              const sel = view === t.id;
              const disabled = t.id !== 'library' && !ctx;
              return (
                <Box component="button" key={t.id} type="button" disabled={disabled}
                  onClick={() => { if (!disabled) setView(t.id); }}
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', px: '12px', py: '6px',
                    border: 'none', borderRadius: '6px', cursor: disabled ? 'default' : 'pointer',
                    fontFamily: 'inherit', fontSize: '12.5px', lineHeight: 1.5,
                    fontWeight: sel ? 600 : 500,
                    color: disabled ? NT.ink4 : (sel ? NT.ink : NT.ink3),
                    bgcolor: sel ? NT.surface : 'transparent',
                    boxShadow: sel ? NT.shadow1 : 'none',
                    opacity: disabled ? 0.5 : 1,
                    transition: 'all .12s',
                    '& svg': { display: 'block', flexShrink: 0 } }}>
                  {t.icon}{t.label}
                </Box>
              );
            })}
          </Box>
        </Toolbar>
      </AppBar>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
        {view === 'library' && (
          <TradeTemplateLibrary trades={trades} tenants={tenants} globalDefault={globalDefault}
            onOpenScope={openScope}
            onSelectTenant={onSelectTenant} />
        )}

        {view === 'builder' && ctx && (
          <>
            {recovery && (
              <Alert severity="info" sx={{ borderRadius: 0 }}
                action={<>
                  <Button color="inherit" size="small" onClick={() => { setDraftTasks(recovery.tasks); setRecovery(null); }}>Resume</Button>
                  <Button color="inherit" size="small" onClick={() => { clearAutosave(); setRecovery(null); }}>Discard</Button>
                </>}>
                You have an unsaved local draft for this template. Resume it?
              </Alert>
            )}
            {dbDraft && (
              <Alert severity="warning" sx={{ borderRadius: 0 }}
                action={<>
                  <Button color="inherit" size="small" onClick={() => {
                    const t = dbDraft.tasks;
                    setDraftTasks(t);
                    setSelectedUid(t[0]?.task_uid ?? null);
                    setActivePhase(t[0]?.phase ?? 'sales');
                    setSeededFrom(null);
                    setDraftLineage(dbDraft.forkedFrom);
                    setEditingDraft(true); // resuming the saved draft → now editing it ("Update draft")
                    setDbDraft(null);
                  }}>Resume draft</Button>
                  <Button color="inherit" size="small" onClick={() => setDbDraft(null)}>Dismiss</Button>
                </>}>
                You have a saved draft for this template{relativeWhen(dbDraft.when ?? '') ? ` (saved ${relativeWhen(dbDraft.when ?? '')})` : ''} that isn’t published yet. Resume to keep editing it.
              </Alert>
            )}
            {seededFrom && (
              <Alert severity={seededFrom === 'global' ? 'warning' : 'info'} sx={{ borderRadius: 0 }}>
                {seededFrom === 'provider'
                  ? 'This tenant has no template for this trade yet — loaded from the trade’s provider default. Edit as needed, then Publish… to your tenant.'
                  : ctx.scope === 'provider'
                    ? 'This trade has no provider default yet — loaded from the Global Default. Edit as needed, then Publish… to establish it as this trade’s provider default (and push to tenants too, if needed).'
                    : 'No template exists for this trade yet — this is the Global Default used by all trades. Add the tasks relevant to this trade, then Publish… — it saves to the provider default (so other tenants adopting this trade start here) and to your tenant.'}
              </Alert>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 1, borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
              <IconButton size="small" onClick={() => setView('library')}><ArrowBackIcon fontSize="small" /></IconButton>
              <Chip size="small" color={ctx.scope === 'provider' ? 'success' : ctx.scope === 'global' ? 'warning' : 'primary'} label={scopeLabel(ctx.scope, ctx.tenantId, tenants)} />
              <Typography variant="body2" color="text.secondary">{trade?.name ?? 'Global Default Template'}</Typography>
              <Chip size="small" variant="outlined" label={ctx.version || 'new'} />
              {(() => {
                const lineage = draftLineage ?? versionData?.forkedFrom ?? null;
                return lineage ? <Chip size="small" color="secondary" variant="outlined" label={`forked from ${forkedFromLabel(lineage, tenants)}`} /> : null;
              })()}
              <Box sx={{ flex: 1 }} />
              {/* ASE engine switch. 1.0 = legacy 14-field template (hides the scheduling editors and
                  omits those fields on save); 2.0 = full CP-SAT contract. */}
              <Tooltip title="ASE 1.0 saves the legacy 14-field template (no workers / compression / dependency class / BOM / system-task). ASE 2.0 saves the full scheduling contract.">
                <ToggleButtonGroup size="small" exclusive value={aseVersion}
                  onChange={(_e, v) => { if (v) setAseVersion(v as AseVersion); }} sx={{ mr: 1 }}>
                  <ToggleButton value="1.0">ASE 1.0</ToggleButton>
                  <ToggleButton value="2.0">ASE 2.0</ToggleButton>
                </ToggleButtonGroup>
              </Tooltip>
              {/* Two actions: keep working on the single in-place draft, or publish it as the new version.
                  The Global Default isn't draft-editable, so its draft button is hidden. */}
              {ctx.scope !== 'global' && (
                <Button size="small" variant="outlined" startIcon={<DriveFileRenameOutlineIcon />} disabled={busy}
                  onClick={() => (hasAnyDependency(currentTasks) ? handleSaveDraft() : setDepWarnFor('draft'))}>
                  {editingDraft ? 'Update draft' : 'Save draft'}
                </Button>
              )}
              <Button size="small" variant="contained" startIcon={<SaveIcon />} disabled={busy}
                onClick={() => (hasAnyDependency(currentTasks) ? setSaveOpen(true) : setDepWarnFor('publish'))}>Publish…</Button>
            </Box>
            <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
              {scopeLoading && currentTasks.length === 0 ? (
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5, color: 'text.secondary' }}>
                  <CircularProgress size={30} />
                  <Typography variant="body2">Loading template…</Typography>
                </Box>
              ) : (
                <SplitPane split="vertical" sizes={paneSizes} onChange={setPaneSizes}
                  // The library's default sash renders a highlight (blue, from its theme CSS) on
                  // hover/drag. We want the drag-to-resize behavior with NO visual change to the
                  // existing thin divider borders the panels already draw — so render a plain,
                  // colorless hit-zone instead of the themed default.
                  sashRender={() => <Box sx={{ width: '100%', height: '100%' }} />}>
                  <Pane minSize={220} maxSize={420}>
                    <TaskOutlineTree tasks={visibleTasks} selectedUid={selectedUid}
                      onSelect={uid => { setSelectedUid(uid); const t = visibleTasks.find(x => x.task_uid === uid); if (t) setActivePhase(t.phase); }}
                      onAddTask={addTask} onAddChild={addChild} onInsertAfter={insertTaskAfter} />
                  </Pane>
                  <Pane minSize={320}>
                    <TaskCanvas templateName={trade?.name ?? 'Global Default Template'} version={ctx.version || 'new'}
                      tasks={visibleTasks} activePhase={activePhase} onPhaseChange={setActivePhase}
                      selectedUid={selectedUid} onSelect={setSelectedUid}
                      onAddTask={addTask} onAddChild={addChild} onDelete={deleteTask} onDuplicate={duplicateTask}
                      onInsertAfter={insertTaskAfter} />
                  </Pane>
                  <Pane minSize={280} maxSize={520}>
                    <TaskInspector task={selectedTask} tasks={visibleTasks} onUpdate={updateTask} aseVersion={aseVersion} />
                  </Pane>
                </SplitPane>
              )}
            </Box>
          </>
        )}

        {view === 'versions' && ctx && (
          <TradeVersionHistory trade={trade ? { name: trade.name, provider: trade.provider } : { name: 'Global Default Template', provider: {} }}
            scope={ctx.scope} tenantId={ctx.tenantId} tenants={tenants} versions={versions} activeSelectedVersion={ctx.version}
            onMakeActive={makeActive}
            onEditVersion={v => { setDraftTasks(null); setCtx(c => c && { ...c, version: v }); setView('builder'); }} />
        )}
      </Box>

      {depWarnFor && (
        <Dialog open onClose={() => setDepWarnFor(null)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningAmberIcon color="warning" fontSize="small" /> No dependencies added
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              None of the tasks in this template have any dependency configured — every task would run
              independently with no ordering between them. This is usually not intended.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDepWarnFor(null)}>Go back and add one</Button>
            <Button variant="contained" color="warning" onClick={() => {
              const action = depWarnFor;
              setDepWarnFor(null);
              if (action === 'publish') setSaveOpen(true); else handleSaveDraft();
            }}>Continue anyway</Button>
          </DialogActions>
        </Dialog>
      )}
      {saveOpen && ctx && (
        <SaveTargetModal open trade={trade} tenants={tenants} currentTenantId={ctx.tenantId}
          recommendProvider={seededFrom === 'global'}
          onClose={() => setSaveOpen(false)} onConfirm={handleSave} />
      )}
      {promote && (
        <PromoteModal open trade={promote.trade} tenant={tenants.find(t => t.id === promote.tenantId) ?? { id: promote.tenantId, name: promote.tenantId }} sourceVersion={promote.sourceVersion}
          onClose={() => setPromote(null)}
          onConfirm={() => {
            const p = promote;
            setPromote(null);
            void run(`${p.trade.name} now has its own Provider default template`, () => persist.promote(p.trade, p.tenantId, p.sourceVersion));
          }} />
      )}
      <Snackbar open={!!toast} autoHideDuration={3500} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={toast?.startsWith('Failed') ? 'error' : 'success'} onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
}

function blankTask(uid: string, name: string, phase: PhaseId, order: number, estimatedDays = 0.01, estimatedHours = 0.25): Task {
  return {
    task_uid: uid, parent_task_uid: deriveParentUid(uid), taskName: name, phase, task_order: order,
    dependencies: [], dependency_classes: {}, estimatedDays, estimatedHours, timeLine: '', taskType: 'Manual Task',
    is_milestone: false, is_system_task: false, is_compressible: false, min_workers: 1, max_workers: 1, compression_factor: 1,
    resources: [{ type: '1099', group: 'labour', count: 1 }], actions: [], bom: [], additionalData: {}, checklistItems: [], valid: true, status: 'active',
  };
}
